import React, { useState, useEffect } from 'react';
import { Wifi, Check, Loader2, Settings, AlertTriangle, Server, Signal } from 'lucide-react';

interface DeviceInfo {
  id: string;
  name: string;
  type: string;
  status: 'available' | 'connecting' | 'connected' | 'error';
  signalStrength: number;
  macAddress: string;
}

interface ConnectedDeviceStatus {
  deviceId: string;
  name: string;
  softwareVersion: string;
  firmwareVersion: string;
  status: 'online' | 'offline' | 'updating';
  lastSeen: Date;
  signalStrength: number;
  ipAddress: string;
  macAddress: string;
  uptime: number;
  sensors: {
    total: number;
    active: number;
  };
}

const DeviceSetup: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [scanning, setScanning] = useState(false);
  const [foundDevices, setFoundDevices] = useState<DeviceInfo[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<DeviceInfo | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [deviceStatus, setDeviceStatus] = useState<ConnectedDeviceStatus | null>(null);

  // Mock device discovery
  useEffect(() => {
    if (scanning) {
      const timer = setTimeout(() => {
        setFoundDevices([
          {
            id: 'SB-001',
            name: 'Scanalytics Basestation',
            type: 'Floor Sensor Hub',
            status: 'available',
            signalStrength: 85,
            macAddress: '00:11:22:33:44:55'
          }
        ]);
        setScanning(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [scanning]);

  // Mock connection process
  const connectToDevice = async (device: DeviceInfo) => {
    setConnecting(true);
    setSelectedDevice(device);
    
    // Simulate connection steps
    await new Promise(resolve => setTimeout(resolve, 2000));
    setConnecting(false);
    setConnected(true);
    setCurrentStep(3);

    // Set mock device status
    setDeviceStatus({
      deviceId: device.id,
      name: device.name,
      softwareVersion: '2.1.0',
      firmwareVersion: '1.4.2',
      status: 'online',
      lastSeen: new Date(),
      signalStrength: device.signalStrength,
      ipAddress: '192.168.1.100',
      macAddress: device.macAddress,
      uptime: 3600, // 1 hour in seconds
      sensors: {
        total: 64,
        active: 64
      }
    });
  };

  const startScan = () => {
    setScanning(true);
    setFoundDevices([]);
  };

  const formatUptime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const getSignalStrengthIcon = (strength: number) => {
    if (strength >= 80) return 'text-green-500';
    if (strength >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const renderStep1 = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-xl font-semibold mb-4">Step 1: Prepare Your Device</h3>
      <div className="space-y-4">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 font-semibold">1</span>
          </div>
          <div>
            <p className="font-medium">Power on your Scanalytics Basestation</p>
            <p className="text-gray-600">Plug in the power adapter and wait for the LED to turn solid blue</p>
          </div>
        </div>
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 font-semibold">2</span>
          </div>
          <div>
            <p className="font-medium">Connect to your WiFi network</p>
            <p className="text-gray-600">Ensure your phone is connected to the same WiFi network</p>
          </div>
        </div>
        <button
          onClick={() => setCurrentStep(2)}
          className="w-full mt-6 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-xl font-semibold mb-4">Step 2: Find Your Device</h3>
      <div className="space-y-4">
        {!scanning && foundDevices.length === 0 && (
          <button
            onClick={startScan}
            className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center"
          >
            <Wifi className="mr-2" size={20} />
            Scan for Devices
          </button>
        )}

        {scanning && (
          <div className="text-center py-8">
            <Loader2 className="animate-spin mx-auto mb-4" size={32} />
            <p className="text-gray-600">Scanning for nearby devices...</p>
          </div>
        )}

        {foundDevices.map(device => (
          <div
            key={device.id}
            className="border rounded-lg p-4 hover:border-blue-500 transition-colors cursor-pointer"
            onClick={() => connectToDevice(device)}
          >
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">{device.name}</h4>
                <p className="text-sm text-gray-600">{device.type}</p>
                <p className="text-xs text-gray-500">MAC: {device.macAddress}</p>
              </div>
              <div className="flex items-center space-x-2">
                <Signal className={getSignalStrengthIcon(device.signalStrength)} size={20} />
                <span className="text-sm text-gray-600">{device.signalStrength}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-xl font-semibold mb-4">Step 3: Device Setup</h3>
      {connecting && (
        <div className="text-center py-8">
          <Loader2 className="animate-spin mx-auto mb-4" size={32} />
          <p className="text-gray-600">Connecting to device...</p>
        </div>
      )}

      {connected && deviceStatus && (
        <div className="space-y-6">
          <div className="flex items-center space-x-2 text-green-500">
            <Check size={24} />
            <span className="font-medium">Successfully connected!</span>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium mb-4">Device Status</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Software Version</p>
                <p className="font-medium">{deviceStatus.softwareVersion}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Firmware Version</p>
                <p className="font-medium">{deviceStatus.firmwareVersion}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <p className="font-medium flex items-center">
                  <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                    deviceStatus.status === 'online' ? 'bg-green-500' : 'bg-red-500'
                  }`}></span>
                  {deviceStatus.status.charAt(0).toUpperCase() + deviceStatus.status.slice(1)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Signal Strength</p>
                <p className="font-medium flex items-center">
                  <Signal className={getSignalStrengthIcon(deviceStatus.signalStrength)} size={16} />
                  <span className="ml-1">{deviceStatus.signalStrength}%</span>
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">IP Address</p>
                <p className="font-medium">{deviceStatus.ipAddress}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Uptime</p>
                <p className="font-medium">{formatUptime(deviceStatus.uptime)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Sensors</p>
                <p className="font-medium">{deviceStatus.sensors.active} / {deviceStatus.sensors.total}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Last Seen</p>
                <p className="font-medium">{deviceStatus.lastSeen.toLocaleTimeString()}</p>
              </div>
            </div>
          </div>

          <button
            onClick={() => {
              // In a real app, this would save the device configuration
              alert('Device successfully added to your account!');
            }}
            className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            Complete Setup
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-2">Add New Device</h2>
        <div className="flex items-center space-x-4">
          {[1, 2, 3].map(step => (
            <div
              key={step}
              className={`flex items-center ${step !== 3 ? 'flex-1' : ''}`}
            >
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center
                ${currentStep === step ? 'bg-blue-500 text-white' : 
                  currentStep > step ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}
              `}>
                {currentStep > step ? <Check size={16} /> : step}
              </div>
              {step !== 3 && (
                <div className={`flex-1 h-1 mx-2 ${
                  currentStep > step ? 'bg-green-500' : 'bg-gray-200'
                }`}></div>
              )}
            </div>
          ))}
        </div>
      </div>

      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}
    </div>
  );
};

export default DeviceSetup; 